/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  SYNCGROUPS_TYPE_SETBBOX,
  SYNCGROUPS_TYPE_SETFEATURES,
  SYNCGROUPS_TYPE_SETTIME,
} from './constants';
import type { SynchronizationGroupState } from './types';

const WARNINGS_TIMELINE_COMPONENT_TYPE = 'WarningsTimeline';
export const WARNING_DIALOG_PANEL_ID = 'warning-dialog';

export type WorkspaceViewsById = Record<string, { componentType?: string }>;

export const getFeatureSourcePanelId = (
  group: SynchronizationGroupState['groups']['byId'][string] | undefined,
): string | undefined => group?.sourcePanelId;

export const isFeatureSource = (
  source: SynchronizationGroupState['sources']['byId'][string] | undefined,
): boolean => !!source?.types.includes(SYNCGROUPS_TYPE_SETFEATURES);

export const isMapSource = (
  source: SynchronizationGroupState['sources']['byId'][string] | undefined,
): boolean =>
  !!source?.types.includes(SYNCGROUPS_TYPE_SETBBOX) &&
  !!source?.types.includes(SYNCGROUPS_TYPE_SETTIME);

export const getMapSourceIds = (
  state: SynchronizationGroupState,
): Set<string> =>
  new Set(
    state.sources.allIds.filter((id) => isMapSource(state.sources.byId[id])),
  );

export const getFeatureSourceIds = (
  state: SynchronizationGroupState,
): string[] =>
  state.sources.allIds.filter((id) => isFeatureSource(state.sources.byId[id]));

export const shouldRemoveFeatureGroupForPanel = (
  panelId: string,
  views?: WorkspaceViewsById,
  isActiveFeatureSource = false,
): boolean => {
  if (panelId === WARNING_DIALOG_PANEL_ID) {
    return true;
  }

  // Active feature sources that are not workspace views (e.g. overlay plugins,
  // whose source id is `${hostPanelId}-${pluginId}`) keep their feature group.
  // Without this, the reconcile would tear down the group `updateLinkedMap`
  // just created, retriggering itself in a remove/recreate loop.
  if (!isActiveFeatureSource && views && !views[panelId]) {
    return true;
  }

  return views?.[panelId]?.componentType === WARNINGS_TIMELINE_COMPONENT_TYPE;
};
